import axios from "axios";
import { AgentWorkState } from "./generated/enums";
import type { TicketId, TicketKey } from "./tickets";
import type { UserId } from "./users";

export { AgentWorkState } from "./generated/enums";

export type AgentSessionId = string & { __brand: "AgentSessionId" };

export type AgentSession = {
    id: AgentSessionId;
    userId: UserId;
    sessionId: string;
    model: string | null;
    repository: string | null;
    branch: string | null;
};

/** An agent session's mark on a ticket, set by the MCP start_work tool. */
export type AgentWork = {
    ticketId: TicketId;
    ticketKey: TicketKey;
    ticketTitle: string;
    /** Null for a session's main agent; subagents each hold a ticket under their own id. */
    agentId: string | null;
    state: AgentWorkState;
    detail: string | null;
    linkedAt: Date;
    stateChangedAt: Date;
    lastActivityAt: Date;
    session: AgentSession;
};

type AgentWorkResponse = Omit<AgentWork, "linkedAt" | "stateChangedAt" | "lastActivityAt"> & {
    linkedAt: string;
    stateChangedAt: string;
    lastActivityAt: string;
};

function formatWork(work: AgentWorkResponse): AgentWork {
    return {
        ...work,
        linkedAt: new Date(work.linkedAt),
        stateChangedAt: new Date(work.stateChangedAt),
        lastActivityAt: new Date(work.lastActivityAt),
    };
}

export type AgentSessionApi = {
    listMine(): Promise<AgentWork[]>;
    forTicket(ticket: TicketId): Promise<AgentWork | null>;
    clearStale(): Promise<void>;
    release(session: AgentSessionId, ticket: TicketId): Promise<void>;
};

export const agentSessionApi: AgentSessionApi = {
    async listMine() {
        const { data } = await axios.get<AgentWorkResponse[]>("/api/v0/agent-sessions");
        return data.map(formatWork);
    },
    async forTicket(ticket) {
        const { data } = await axios.get<AgentWorkResponse | "">(
            `/api/v0/tickets/${ticket}/agent-work`
        );
        return data ? formatWork(data) : null;
    },
    async clearStale() {
        await axios.delete("/api/v0/agent-sessions/stale");
    },
    async release(session, ticket) {
        await axios.delete(`/api/v0/agent-sessions/${session}/tickets/${ticket}`);
    },
};
